import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./ConsultationFormScreen.css";
import SmartInput from "../components/SmartInput";
import ProgressGlowBar from "../components/ProgressGlowBar";
import { API_BASE_URL, API_GEO_URL, OPENCAGE_API_KEY } from "../config";
import { Link } from "react-router-dom";


import {
  industryOptions,
  revenueOptions,
  areaOptionsByIndustry,
  generalAreaOptions,
  stateOptions,
  topicOptions,
} from "../constants/formOptions";

const ConsultationFormScreen = ({
  addBotMessage,
  setUserId,
  fetchCredits,
  setShowOptions,
  setIsLocked,
}) => {
  const navigate = useNavigate();
  const [formStep, setFormStep] = useState(1);
  const [errors, setErrors] = useState([]);
  const [showTerms, setShowTerms] = useState(true);

  const [form, setForm] = useState({
    userId: null,
    industry: "",
    area: "",
    stage: "",
    topic: "",
    country: "",
    state: "",
    businessDesc: "",
    businessGoal: "",
    targetAudience: "",
    teamSize: "",
    revenueRange: "",
    registrationType: "",
    biggestChallenge: "",
    whatTried: "",
    supportArea: "",
    usedAITools: "",
    website: "",
    linkedin: "",
    facebook: "",
    instagram: "",
    twitter: "",
    youtube: "",
    phone: "",
    email: "",
    gstNumber: "",
    consentToUse: "",
    wantReview: "",
    optInFor: "",
  });

  useEffect(() => {
    document.body.style.overflow = showTerms ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showTerms]);

  useEffect(() => {
    const fetchUserAndLocation = async () => {
      try {
        // 🔐 Get user from localStorage
        const userData = localStorage.getItem("user");
        if (userData) {
          const parsed = JSON.parse(userData);
          setForm((prev) => ({ ...prev, userId: parsed.id }));
        }
      } catch (err) {
        console.error("❌ Failed to fetch user from localStorage:", err);
      }

      // 📍 Get user's geolocation and reverse geocode
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const lat = position.coords.latitude;
            const lon = position.coords.longitude;
            const url = `https://api.opencagedata.com/geocode/v1/json?q=${lat}+${lon}&key=${OPENCAGE_API_KEY}`;

            try {
              const response = await fetch(url);
              const data = await response.json();

              if (data?.results?.length > 0) {
                const components = data.results[0].components;
                const country = components.country || "";
                const state = components.state || components.region || "";

                setForm((prev) => ({
                  ...prev,
                  country,
                  state,
                }));
              }
            } catch (err) {
              console.error("🌐 Reverse geocoding failed:", err);
            }
          },
          (error) => {
            console.warn("❌ Geolocation error:", error);
            // Optional: set fallback country/state
          }
        );
      }
    };

    fetchUserAndLocation();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };

      // 👇 Auto-set revenueRange if stage is "Idea Stage"
      if (field === "stage" && value === "Idea Stage") {
        updated.revenueRange = "0-10000";
      }

      return updated;
    });
  };

  const stepRequiredFields = {
    1: ["industry", "area", "country", "state"],
    2: ["website", "linkedin", "facebook", "instagram", "twitter", "youtube"],
    3: [
      "businessDesc",
      "businessGoal",
      "targetAudience",
      "teamSize",
      "revenueRange",
      "registrationType",
      "biggestChallenge",
      "whatTried",
      "supportArea",
      "usedAITools",
    ],
    4: ["phone", "email", "gstNumber"],
  };

  const validateStep = () => {
    const fields = stepRequiredFields[formStep] || [];
    const missing = fields.filter((field) => !form[field]);

    if (missing.length > 0) {
      alert(
        "Validation Errors:\n" +
          missing
            .map((f) => `❗ Please fill the required field: ${f}`)
            .join("\n")
      );
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setFormStep((prev) => prev + 1);
    }
  };

  const handleStartConsultation = async () => {
    setErrors([]);

    try {
      let storedUserId = localStorage.getItem("user_id");

      if (!storedUserId) {
        storedUserId = uuidv4();
        localStorage.setItem("user_id", storedUserId);
      }

      const payload = {
        ...form,
        userId: storedUserId,
        user_id: storedUserId,
      };

      const res = await axios.post(`${API_BASE_URL}/submit-form`, payload);

      if (res.status === 200 && res.data?.user_id) {
        if (res.data.user_id !== storedUserId) {
          localStorage.setItem("user_id", res.data.user_id);
        }

        setUserId(res.data.user_id);
        fetchCredits(res.data.user_id);
        setShowOptions(true);
        if (res.data.locked) setIsLocked(true);

        const initialMessages = [
          {
            sender: "bot",
            text: `✅ Got it! You're interested in ${form.topic} for your ${form.industry} business in the ${form.area} domain.`,
          },
          {
            sender: "bot",
            text: "💡 Could you now describe the specific business problem or challenge you're facing?",
          },
        ];

        localStorage.setItem(
          "initialMessages",
          JSON.stringify(initialMessages)
        );
        navigate("/chat");
      } else {
        setErrors(["⚠️ Unexpected response from server."]);
      }
    } catch (err) {
      console.error("❌ Error submitting form:", err);
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setErrors(detail.map((d) => `${d.loc?.join(".")}: ${d.msg}`));
      } else {
        setErrors([detail || "🚫 Something went wrong."]);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const renderFormStep = () => {
    switch (formStep) {
      case 1:
        return (
          <>
            <SmartInput
              id="industry"
              label="Business Industry "
              value={form.industry}
              onChange={(e) => handleChange("industry", e.target.value)}
              options={industryOptions}
            />
            <SmartInput
              id="area"
              label="Business Category/Area "
              value={form.area}
              onChange={(e) => handleChange("area", e.target.value)}
              options={
                areaOptionsByIndustry[form.industry] || generalAreaOptions
              }
            />
            <SmartInput
              id="stage"
              label="Stage of Business "
              value={form.stage}
              onChange={(e) => handleChange("stage", e.target.value)}
              options={[
                "Idea Stage",
                "Startup",
                "Early Growth",
                "Scaling",
                "Established",
                "Mature Enterprise",
                "Pivot / Reboot",
              ]}
            />
            <SmartInput
              id="topic"
              label="Consultation Topic"
              value={form.topic}
              onChange={(e) => handleChange("topic", e.target.value)}
              options={topicOptions}
            />
            <SmartInput
              id="country"
              label="Country "
              value={form.country}
              onChange={(e) => handleChange("country", e.target.value)}
              options={["India", "USA", "UK", "Other"]}
            />
            <SmartInput
              id="state"
              label="State "
              value={form.state}
              onChange={(e) => handleChange("state", e.target.value)}
              options={stateOptions}
            />
          </>
        );
      case 2:
        return (
          <>
            <SmartInput
              id="website"
              label="Company Website, Else select Opt."
              value={form.website}
              onChange={(e) => handleChange("website", e.target.value)}
              options={["Not created planing to build", "Not created"]}
            />
            <SmartInput
              id="linkedin"
              label="LinkedIn Link, Else select Opt."
              value={form.linkedin}
              onChange={(e) => handleChange("linkedin", e.target.value)}
              options={["Not created planing to build", "Not created"]}
            />
            <SmartInput
              id="facebook"
              label="Facebook Link, Else select Opt."
              value={form.facebook}
              onChange={(e) => handleChange("facebook", e.target.value)}
              options={["Not created planing to build", "Not created"]}
            />
            <SmartInput
              id="instagram"
              label="Instagram Link, Else select Opt."
              value={form.instagram}
              onChange={(e) => handleChange("instagram", e.target.value)}
              options={["Not created planing to build", "Not created"]}
            />
            <SmartInput
              id="twitter"
              label="Twitter Link, Else select Opt."
              value={form.twitter}
              onChange={(e) => handleChange("twitter", e.target.value)}
              options={["Not created planing to build", "Not created"]}
            />
            <SmartInput
              id="youtube"
              label="YouTube Link, Else select Opt."
              value={form.youtube}
              onChange={(e) => handleChange("youtube", e.target.value)}
              options={["Not created planing to build", "Not created"]}
            />
          </>
        );
      case 3:
        return (
          <>
            <SmartInput
              id="businessDesc"
              label="Describe Your Business "
              value={form.businessDesc}
              onChange={(e) => handleChange("businessDesc", e.target.value)}
            />
            <SmartInput
              id="businessGoal"
              label="Your Goal Short-Term or long-Term "
              value={form.businessGoal}
              onChange={(e) => handleChange("businessGoal", e.target.value)}
            />
            <SmartInput
              id="targetAudience"
              label="What is Target Audience "
              value={form.targetAudience}
              onChange={(e) => handleChange("targetAudience", e.target.value)}
              options={[
                "B2B (Businesses)",
                "B2C (Consumers)",
                "Startups",
                "SMEs (Small & Medium Enterprises)",
                "Enterprises",
                "Students & Learners",
                "Professionals & Freelancers",
                "Developers & Tech Enthusiasts",
                "Healthcare Providers",
                "Educational Institutions",
                "E-commerce Customers",
                "Local Communities",
                "Government or NGOs",
                "Global Market",
                "Not sure yet",
              ]}
            />
            <SmartInput
              id="teamSize"
              label="Select your Team Size in Range"
              value={form.teamSize}
              onChange={(e) => handleChange("teamSize", e.target.value)}
              options={[
                "0-10",
                "10-50",
                "50-100",
                "100-500",
                "500-1000",
                "1000-5000",
              ]}
            />
            <SmartInput
              id="revenueRange"
              label="Select Revenue-Range Till Now  "
              value={form.revenueRange}
              onChange={(e) => handleChange("revenueRange", e.target.value)}
              options={revenueOptions}
            />
            <SmartInput
              id="registrationType"
              label="Company Registration Type "
              value={form.registrationType}
              onChange={(e) => handleChange("registrationType", e.target.value)}
              options={[
                "Unregistered",
                "OPC",
                "Pvt Ltd",
                "LLP",
                "Proprietorship",
              ]}
            />
            <SmartInput
              id="biggestChallenge"
              label="Biggest Challenge You’re Facing "
              value={form.biggestChallenge}
              onChange={(e) => handleChange("biggestChallenge", e.target.value)}
              options={[
                "Lack of Technical Expertise",
                "Limited Budget or Resources",
                "Low Customer Engagement",
                "Scaling Operations",
                "Marketing and Visibility",
                "Data Management",
                "Automation Challenges",
                "Finding Product-Market Fit",
                "Time Constraints",
                "Team Collaboration Issues",
                "Customer Retention",
                "Generating Leads",
                "High Operational Costs",
                "Understanding AI Tools",
                "Building Online Presence",
                "Hiring the Right Talent",
                "Not sure yet",
              ]}
            />
            <SmartInput
              id="whatTried"
              label="What Have You Tried So Far? Else select Opt."
              value={form.whatTried}
              onChange={(e) => handleChange("whatTried", e.target.value)}
              options={["Not yet"]}
            />
            <SmartInput
              id="supportArea"
              label="Which Area Needs the Most Support? "
              value={form.supportArea}
              onChange={(e) => handleChange("supportArea", e.target.value)}
              options={[
                "Marketing & Lead Generation",
                "Sales & Conversion",
                "Funding & Investment",
                "Product Development",
                "Hiring & Talent",
                "Customer Acquisition & Retention",
                "Tech & Platform Support",
                "Legal & Compliance",
                "Operations & Scaling",
                "Financial Planning & Revenue Model",
                "Business Strategy & Mentorship",
                "Something Else",
                "Not yet",
              ]}
            />
            <SmartInput
              id="usedAITools"
              label="Used AI Tools Before? If yes, which? Else select Opt."
              value={form.usedAITools}
              onChange={(e) => handleChange("usedAITools", e.target.value)}
              options={[
                "ChatGPT",
                "Bard / Gemini",
                "Claude",
                "Midjourney",
                "DALL·E",
                "Stable Diffusion",
                "GitHub Copilot",
                "Notion AI",
                "Jasper",
                "Copy.ai",
                "Runway",
                "Synthesia",
                "Pictory",
                "Other",
                "Not yet",
              ]}
            />
          </>
        );
      case 4:
        return (
          <>
            <SmartInput
              id="phone"
              label="please share Business Number"
              value={form.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
            />
            <SmartInput
              id="email"
              label="Business Email, Else select Opt."
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              options={["Not created planning to build", "Not created"]}
            />
            <SmartInput
              id="gstNumber"
              label="please share GST Number, Else select Opt."
              value={form.gstNumber}
              onChange={(e) => handleChange("gstNumber", e.target.value)}
              options={["Not created planning to build", "Not created"]}
            />
            <div className="last-page">
              <h2>With Stratxcel by your side, stress takes flight</h2>
              <button
                className="primary-button"
                onClick={handleStartConsultation}
              >
                Generate AI Expert Analysis
              </button>
              <button className="primary-button" onClick={() => setFormStep(3)}>
                Back
              </button>
            </div>
          </>
        );
    }
  };

  const TermsModal = () => (
    <div className="terms-overlay">
      <div className="terms-modal">
        <h2>Terms & Conditions</h2>
        <p>
          By proceeding, you agree to our{" "}
          <Link to="/privacy-policy" target="_blank" rel="noreferrer">
            Privacy Policy
          </Link>{" "}
          and{" "}
          <Link to="/terms" target="_blank" rel="noreferrer">
            Terms & Conditions
          </Link>
          . We may store and use the information you provide for business
          analysis, communication, and service improvement purposes.
        </p>
        <div className="terms-actions">
          <button
            className="primary-button"
            onClick={() => setShowTerms(false)}
          >
            I Agree
          </button>
          <button className="primary-button" onClick={() => navigate("/")}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {showTerms && <TermsModal />}

      <div className={`form-screen ${showTerms ? "blurred" : ""}`}>
        <div className="glow-highlight"></div>
        <div className="branding-left">StratXcel.AI</div>

        <div className="form-box">
          <h1>GET YOUR FREE CONSULTATION</h1>
          <ProgressGlowBar currentStep={formStep} totalSteps={4} />
          <div className="form-container">{renderFormStep()}</div>

          <div className="form-nav-buttons">
            {formStep > 1 && formStep < 4 && (
              <button
                className="primary-button"
                onClick={() => setFormStep(formStep - 1)}
              >
                Back
              </button>
            )}
            {formStep < 4 && (
              <button className="primary-button" onClick={handleNext}>
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ConsultationFormScreen;
